/**
 * Whether the app can currently reach the auction.
 *
 * WHY THIS IS NOT A NETWORK LIBRARY. The obvious move is `NetInfo` or
 * `expo-network` — ask the phone whether it has a connection. That answers the
 * wrong question. What a bidder needs to know is not "is the radio up" but "can
 * I still reach the sale", and those come apart in exactly the situations that
 * cost money:
 *
 *   - hotel and airport captive portals report a healthy connection while every
 *     request 302s to a login page;
 *   - a VPN dropping mid-session leaves the radio perfectly happy;
 *   - our own backend being down is invisible to the phone entirely.
 *
 * In each case the radio says "online" and the bidder cannot bid. So connection
 * state here is derived from whether OUR OWN requests are succeeding. It also
 * costs no new native dependency, which on this project is not a small thing —
 * every native module added is another surface in an iOS release pipeline with
 * a documented history of biting.
 *
 * THE ESCALATION IS THE DESIGN. From the bidder handoff: "Most drops resolve in
 * seconds, so Reconnecting changes nothing except a quiet strip — disabling the
 * bid button on a two-second blip is worse than the blip. Only a sustained drop
 * freezes the room."
 *
 *   online        everything normal
 *   reconnecting  something failed; say so quietly, disable NOTHING
 *   offline       sustained failure; freeze the room, because every number on
 *                 screen is now a claim we cannot stand behind
 */

export type ConnectionState = 'online' | 'reconnecting' | 'offline';

/**
 * How long a failing connection stays `reconnecting` before it is called
 * `offline`. Long enough that a subway tunnel or a lift does not freeze the
 * room; short enough that a bidder is not left reading stale prices as if they
 * were live.
 */
export const OFFLINE_AFTER_MS = 8_000;

/**
 * Consecutive failures that call it offline regardless of the clock. A single
 * timeout is noise; three in a row is not, however fast they arrived.
 */
export const OFFLINE_AFTER_FAILURES = 3;

export interface ConnectionSnapshot {
  state: ConnectionState;
  /**
   * When we last heard from the server. Everything on screen is only as true as
   * this moment — which is why the offline surfaces stamp it ("Prices as of
   * 9:39") rather than letting the bidder assume the numbers are current.
   */
  lastOkAt: number | null;
  /** Consecutive failures since the last success. */
  failures: number;
}

type Listener = (snapshot: ConnectionSnapshot) => void;

/**
 * The monitor is deliberately a plain object rather than a module singleton with
 * hidden state: tests need to construct one, drive it, and throw it away. A
 * singleton would make every test in the suite share a connection.
 */
export interface ConnectionMonitor {
  snapshot: () => ConnectionSnapshot;
  /** A request reached the server. Clears any failure run. */
  reportOk: (now?: number) => void;
  /** A request failed to reach the server. NOT for 4xx — see `isReachabilityFailure`. */
  reportFailure: (now?: number) => void;
  /**
   * Re-evaluate against the clock. `reconnecting` becomes `offline` purely by
   * elapsed time, so something has to ask — nothing here runs a timer of its
   * own, because a timer in a shared module is a battery note in App Review.
   */
  tick: (now?: number) => void;
  subscribe: (listener: Listener) => () => void;
}

export function createConnectionMonitor(now: () => number = () => Date.now()): ConnectionMonitor {
  let state: ConnectionState = 'online';
  let lastOkAt: number | null = null;
  let failures = 0;
  let firstFailureAt: number | null = null;
  const listeners = new Set<Listener>();

  const snapshot = (): ConnectionSnapshot => ({ state, lastOkAt, failures });

  const emit = (previous: ConnectionState) => {
    if (previous === state) return;
    const snap = snapshot();
    for (const l of listeners) l(snap);
  };

  const evaluate = (at: number) => {
    const previous = state;
    if (failures === 0) {
      state = 'online';
    } else if (
      failures >= OFFLINE_AFTER_FAILURES ||
      (firstFailureAt !== null && at - firstFailureAt >= OFFLINE_AFTER_MS)
    ) {
      state = 'offline';
    } else {
      state = 'reconnecting';
    }
    emit(previous);
  };

  return {
    snapshot,
    reportOk: (at = now()) => {
      failures = 0;
      firstFailureAt = null;
      lastOkAt = at;
      evaluate(at);
    },
    reportFailure: (at = now()) => {
      failures += 1;
      firstFailureAt ??= at;
      evaluate(at);
    },
    tick: (at = now()) => evaluate(at),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Is this error the server being unreachable, rather than the server saying no?
 *
 * The distinction is the whole point. A refused bid, an expired session, a
 * permission error — those are the server working correctly, and treating them
 * as a connection drop would freeze the room over a rejection the bidder should
 * simply read. Only a failure to get an answer at all counts.
 */
export function isReachabilityFailure(error: unknown): boolean {
  if (error === null || error === undefined) return false;

  // A PostgREST/Supabase error carrying an HTTP status is an ANSWER. The server
  // was reached; it disagreed.
  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' && status >= 400) return false;

  // Narrowed rather than coerced: `String(someObject)` yields "[object Object]",
  // which would then be matched against network wording and always miss.
  const raw = error instanceof Error ? error.message : (error as { message?: unknown }).message;
  const message = (typeof raw === 'string' ? raw : '').toLowerCase();

  return (
    // OUR OWN timeout, from `withTimeout` in client.ts: "The server did not
    // respond within 20s." It matches none of the generic phrases below, which
    // meant the single most likely reachability failure in practice — a request
    // that never comes back — was classified as an ordinary error. Caught
    // 2026-08-26 by a test asserting a timed-out bid becomes unconfirmed.
    //
    // Matching our own wording is coupling, and it is the lesser evil: the
    // alternative is threading an error NAME through `placeBid`'s
    // `{ success: false, reason, message }` shape, which every call site and
    // both surfaces already depend on. If that message is ever reworded, this
    // line and the timeout test fail together.
    message.includes('did not respond') ||
    message.includes('network request failed') ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('econnrefused') ||
    message.includes('enotfound') ||
    message.includes('unable to connect')
  );
}
